import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { supabase } from '../lib/supabase';
import PageHeader from '../components/PageHeader';
import { formatDate, formatDateTime } from '../lib/format';
import { useConfirm } from '../components/ConfirmDialog';

// Chat between donors / visitors (identified by device_id from the
// mobile app) and the community-chat agent. v2 adds:
//   * status (OPEN / CLOSED) with a filter on the thread list
//   * opened_at + closed_at timestamps
//   * "قفل الاستفسار" action (calls chat-close edge function)
//   * assigned_agent_id shown when set
//
// The name + phone are captured on the mobile side before the very
// first message can be sent (chat-send enforces it), so the
// admin always sees a real contact — no more "unknown device_id" rows.

type Thread = {
  id: string;
  device_id: string;
  contact_name: string;
  contact_phone: string;
  last_message_at: string;
  admin_unread: number;
  device_unread: number;
  archived: boolean;
  created_at: string;
  status: 'OPEN' | 'CLOSED';
  opened_at: string;
  closed_at: string | null;
  closed_by: string | null;
  assigned_agent_id: string | null;
};

type Message = {
  id: string;
  thread_id: string;
  sender: 'device' | 'admin';
  text: string;
  created_at: string;
  admin_user_id: string | null;
};

type StatusFilter = 'OPEN' | 'CLOSED' | 'ALL';

// user_id → display name. Operator wanted the full e-mail visible in
// "بواسطة" (e.g. admin@mersal-ngo.org), not just the local part —
// makes disambiguating cross-org admins in the future easier. Falls
// back to the raw uuid when we couldn't load the row — the roster
// fetch now goes through list_app_admin_names() so residual causes
// are a since-deleted admin account or the RPC call itself erroring
// (schema cache miss after a fresh deploy, grant drift, etc.);
// those errors are logged via console.warn in the loader.
function shortName(email: string | undefined | null, uuid: string | null | undefined): string {
  if (email && email.length > 0) return email;
  if (uuid) return uuid.slice(0, 8);
  return '—';
}

export default function CommunityChatPage() {
  const [threads, setThreads] = useState<Thread[]>([]);
  const [selected, setSelected] = useState<Thread | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('OPEN');
  const [closing, setClosing] = useState(false);
  const confirm = useConfirm();
  // Cache of admin user_id → email, so the header can show who opened
  // (first replied) and who closed each thread with a friendly name.
  const [adminNames, setAdminNames] = useState<Record<string, string>>({});
  const nameFor = (uuid: string | null | undefined) =>
    shortName(uuid ? adminNames[uuid] : undefined, uuid ?? null);
  // Per-thread metadata (opened_at, closed_at, closed_by) for every
  // thread that belongs to the currently-selected identity. Populated
  // by loadConversationByPhone so the timeline dividers between
  // successive inquiries can carry the exact open/close info for the
  // NEXT thread instead of a single header line that only reflects
  // the representative thread.
  const [phoneThreads, setPhoneThreads] = useState<Thread[]>([]);

  // Load the admin roster once at mount for uuid -> email attribution
  // in the chat log. Small table (few rows), no paging needed.
  //
  // Routed through list_app_admin_names() RPC rather than a direct
  // SELECT on `app_admins`:
  //   - A direct SELECT hits the `app_admins read (self or super)`
  //     policy and returns only the caller's own row for a non-super
  //     chat-admin, leaving every other agent's reply in the log
  //     rendered as a raw uuid.
  //   - list_app_admin_names() is SECURITY DEFINER + gated on
  //     app_admins membership, and returns only {user_id, email}.
  //     The sibling list_app_admins_with_activity() RPC would also
  //     fix the uuid gap but exposes every admin's full permission
  //     matrix (is_super_admin, HR approver flags, etc.) through
  //     the browser's network tab -- a surface only TeamPage needs,
  //     not CommunityChatPage.
  useEffect(() => {
    (async () => {
      const { data, error } = await supabase.rpc('list_app_admin_names');
      if (error) {
        console.warn('list_app_admin_names failed; chat log will show raw uuids', error);
        return;
      }
      if (!data) return;
      const map: Record<string, string> = {};
      for (const row of data as Array<{ user_id: string; email: string | null }>) {
        map[row.user_id] = row.email ?? '';
      }
      setAdminNames(map);
    })();
  }, []);

  useEffect(() => { void loadThreads(); }, [statusFilter]);
  useEffect(() => {
    if (selected) void loadConversationByPhone(selected.contact_phone, selected.id);
  }, [selected]);

  // U-ADM-07 · pre-launch audit hardening. Realtime subscription so
  // the inbox reflects new visitor messages (and the unread badge)
  // the second they hit the DB, without the agent having to change
  // the filter or reload. Refetch is intentionally coarse
  // (loadThreads + loadMessages for the selected thread) because
  // both queries are cheap and the payload we would build from the
  // change events isn't guaranteed to match the same select shape.
  useEffect(() => {
    const channel = supabase
      .channel('chat-live')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'chat_threads' },
        () => { void loadThreads(); },
      )
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'chat_messages' },
        (payload) => {
          // Refetch the merged conversation for this phone whenever a
          // new message lands on any of its threads — otherwise the
          // agent misses a returning visitor's fresh thread.
          const threadId = (payload.new as { thread_id?: string })?.thread_id;
          if (selected && threadId) {
            const belongsToPhone = threads.some(
              (t) => t.id === threadId && t.contact_phone === selected.contact_phone,
            ) || threadId === selected.id;
            if (belongsToPhone) {
              void loadConversationByPhone(selected.contact_phone, selected.id);
            } else {
              void loadThreads();
            }
          } else {
            void loadThreads();
          }
        },
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id, statusFilter]);

  const loadThreads = async () => {
    setLoading(true);
    // Archive was retired per operator (2026-09-02): every thread is
    // either OPEN or CLOSED — nothing gets hidden. The `archived`
    // column stays in the schema but we don't filter on it anymore.
    let q = supabase
      .from('chat_threads')
      .select('*')
      .order('last_message_at', { ascending: false });
    if (statusFilter !== 'ALL') q = q.eq('status', statusFilter);
    const { data, error: err } = await q;
    if (err) setError(err.message);
    else setThreads((data ?? []) as Thread[]);
    setLoading(false);
  };

  // Load the entire history for this visitor by contact_phone —
  // merging every past thread into one timeline so a returning
  // visitor's follow-up on a new day sits next to their earlier
  // conversation with a date + "new inquiry" divider between them.
  // Only the currently-focused thread's admin_unread is cleared so
  // an older CLOSED thread doesn't relight the badge on its
  // sibling threads.
  const loadConversationByPhone = async (phone: string, focusThreadId: string) => {
    const { data: threadRows, error: threadsErr } = await supabase
      .from('chat_threads')
      .select('*')
      .eq('contact_phone', phone)
      .order('created_at', { ascending: true });
    if (threadsErr) {
      setError(threadsErr.message);
      return;
    }
    const rows = (threadRows ?? []) as Thread[];
    setPhoneThreads(rows);
    const threadIds = rows.map((t) => t.id);
    if (threadIds.length === 0) {
      setMessages([]);
      return;
    }
    const { data, error: err } = await supabase
      .from('chat_messages')
      .select('id, thread_id, sender, text, admin_user_id, created_at')
      .in('thread_id', threadIds)
      .order('created_at');
    if (err) setError(err.message);
    else {
      setMessages((data ?? []) as Message[]);
      await supabase.from('chat_threads').update({ admin_unread: 0 }).eq('id', focusThreadId);
      void loadThreads();
    }
  };

  const send = async (e: FormEvent) => {
    e.preventDefault();
    if (!selected || !draft.trim()) return;
    if (selected.status === 'CLOSED') {
      setError('المحادثة اتقفلت — مينفعش نبعت رد.');
      return;
    }
    const text = draft.trim();
    setDraft('');
    // Attach the current admin's user_id so we can render "أول رد من"
    // in the thread header and audit who replied to what. Only inserted
    // when we actually have a session — public/test paths without a
    // signed-in user still work (admin_user_id stays null).
    const { data: sess } = await supabase.auth.getSession();
    const adminUid = sess?.session?.user?.id ?? null;
    const { error: err } = await supabase.from('chat_messages').insert({
      thread_id: selected.id,
      sender: 'admin',
      text,
      admin_user_id: adminUid,
    });
    if (err) return setError(err.message);
    await supabase.from('chat_threads').update({
      last_message_at: new Date().toISOString(),
      device_unread: (selected.device_unread ?? 0) + 1,
    }).eq('id', selected.id);
    // Fire-and-forget direct push to the donor's phone. RPC no-ops if
    // the thread never linked to a registered FCM token, so older
    // conversations just don't get a ring — the message still lands
    // and the donor sees it on next poll / app open.
    supabase
      .rpc('enqueue_admin_chat_reply', { p_thread_id: selected.id, p_body: text })
      .then(({ error: rpcErr }) => {
        if (rpcErr) console.warn('enqueue_admin_chat_reply failed', rpcErr);
      });
    await loadConversationByPhone(selected.contact_phone, selected.id);
  };

  // Close via the edge function so closed_at + closed_by land server-side
  // with the caller's JWT — keeps the audit trail honest and lets us
  // enforce the can_manage_chat permission in one place.
  const closeThread = async () => {
    if (!selected) return;
    if (!(await confirm({
      title: 'تقفل الاستفسار؟',
      body: 'الزائر مش هيقدر يبعت تاني ف نفس المحادثة.',
      confirmText: 'اقفل',
      tone: 'danger',
    }))) return;
    setClosing(true);
    setError(null);
    const { data, error: err } = await supabase.functions.invoke<{ ok?: boolean; error?: string }>(
      'chat-close',
      { body: { thread_id: selected.id } },
    );
    setClosing(false);
    if (err || !data?.ok) {
      setError(err?.message ?? data?.error ?? 'مقدرناش نقفل المحادثة');
      return;
    }
    await loadThreads();
    // Refresh the selected thread from the freshly loaded list.
    const { data: fresh } = await supabase
      .from('chat_threads')
      .select('*')
      .eq('id', selected.id)
      .single();
    if (fresh) setSelected(fresh as Thread);
  };

  // Compute the rendered timeline: messages + dividers.
  // A divider is inserted whenever the day changes OR the thread
  // changes, so a returning visitor's next-day inquiry sits under a
  // clear "استفسار جديد · <date>" band.
  type TimelineItem =
    | { kind: 'message'; msg: Message }
    | { kind: 'day'; label: string }
    | { kind: 'thread'; thread: Thread };
  const threadById = useMemo(() => {
    const m = new Map<string, Thread>();
    for (const t of phoneThreads) m.set(t.id, t);
    return m;
  }, [phoneThreads]);
  const timeline = useMemo<TimelineItem[]>(() => {
    const items: TimelineItem[] = [];
    let lastDay: string | null = null;
    let lastThreadId: string | null = null;
    for (const m of messages) {
      const day = new Date(m.created_at).toISOString().slice(0, 10);
      const dayChanged = day !== lastDay;
      const threadChanged = m.thread_id !== lastThreadId;
      if (threadChanged) {
        // Emit a per-thread divider — carries that thread's own
        // opened_at / closed_at / closed_by so each inquiry's own
        // lifecycle is visible in the timeline (per the operator's
        // 2026-09-17 request). Fires for the FIRST thread too so the
        // very first inquiry gets its own header instead of a bare
        // day chip.
        const t = threadById.get(m.thread_id);
        if (t) items.push({ kind: 'thread', thread: t });
      } else if (dayChanged) {
        items.push({ kind: 'day', label: formatDate(m.created_at) });
      }
      items.push({ kind: 'message', msg: m });
      lastDay = day;
      lastThreadId = m.thread_id;
    }
    return items;
  }, [messages, threadById]);

  const totalUnread = useMemo(
    () => threads.reduce((a, t) => a + (t.status === 'OPEN' ? (t.admin_unread ?? 0) : 0), 0),
    [threads],
  );

  // Counts have to be OVERALL counts, not "counts within the current
  // filter". If we compute them from `threads` (which is already
  // filtered by statusFilter), then "closed" tab shows only its own
  // closed count and the open/all pills stay empty. Fetch them
  // separately so every tab shows its own total regardless of which
  // one is currently active.
  const [tabCounts, setTabCounts] = useState<{ open: number; closed: number; all: number }>({ open: 0, closed: 0, all: 0 });
  const refreshTabCounts = async () => {
    const [openRes, closedRes, allRes] = await Promise.all([
      supabase.from('chat_threads').select('id', { count: 'exact', head: true }).eq('status', 'OPEN'),
      supabase.from('chat_threads').select('id', { count: 'exact', head: true }).eq('status', 'CLOSED'),
      supabase.from('chat_threads').select('id', { count: 'exact', head: true }),
    ]);
    setTabCounts({
      open: openRes.count ?? 0,
      closed: closedRes.count ?? 0,
      all: allRes.count ?? 0,
    });
  };
  useEffect(() => { void refreshTabCounts(); }, [statusFilter]);

  // Collapse threads into one row per contact_phone.
  // Operator's model (2026-09-16): phone is the identity; the typed
  // name is display-only, so اسامع and اسامه on the same number are
  // the same person. Donor mobile spawns a new thread on every fresh
  // inquiry, but the admin should see ONE row per phone — carrying
  // the inquiry count as a chip and aggregating the unread badges.
  // Picking a row loads the merged conversation via
  // loadConversationByPhone — the timeline dividers keep each
  // inquiry visually distinct.
  type IdentityRow = {
    key: string;
    representative: Thread;
    count: number;
    unread: number;
    anyOpen: boolean;
    latestAt: string;
  };
  const identityRows = useMemo<IdentityRow[]>(() => {
    const byKey = new Map<string, IdentityRow>();
    for (const t of threads) {
      const key = t.contact_phone;
      const cur = byKey.get(key);
      if (!cur) {
        byKey.set(key, {
          key,
          representative: t,
          count: 1,
          unread: t.admin_unread ?? 0,
          anyOpen: t.status === 'OPEN',
          latestAt: t.last_message_at,
        });
      } else {
        cur.count += 1;
        cur.unread += t.admin_unread ?? 0;
        cur.anyOpen = cur.anyOpen || t.status === 'OPEN';
        if (t.last_message_at > cur.latestAt) {
          cur.latestAt = t.last_message_at;
          // Prefer the most recent thread as the representative so
          // clicking the row lands on the latest inquiry — which is
          // the one the admin is most likely replying to.
          cur.representative = t;
        }
      }
    }
    return Array.from(byKey.values()).sort((a, b) => (a.latestAt < b.latestAt ? 1 : -1));
  }, [threads]);

  return (
    <>
      <PageHeader
        eyebrow="الدردشة"
        title={`دردشة الزوار${totalUnread > 0 ? ` — ${totalUnread} غير مقروءة` : ''}`}
        subtitle="الرسائل جاية من الموبايل — الزائر بيكتب اسمه ورقمه قبل ما يبعت أول رسالة"
      />
      {error && <div className="error-inline">{error}</div>}

      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <button
          className={`btn btn-small ${statusFilter === 'OPEN' ? '' : 'btn-secondary'}`}
          onClick={() => setStatusFilter('OPEN')}
        >
          مفتوحة ({tabCounts.open})
        </button>
        <button
          className={`btn btn-small ${statusFilter === 'CLOSED' ? '' : 'btn-secondary'}`}
          onClick={() => setStatusFilter('CLOSED')}
        >
          مقفولة ({tabCounts.closed})
        </button>
        <button
          className={`btn btn-small ${statusFilter === 'ALL' ? '' : 'btn-secondary'}`}
          onClick={() => setStatusFilter('ALL')}
        >
          الكل ({tabCounts.all})
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '340px 1fr', gap: 16, minHeight: 500 }}>
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {loading ? (
            <div className="empty">جاري التحميل...</div>
          ) : identityRows.length === 0 ? (
            <div className="empty">مفيش محادثات {statusFilter === 'OPEN' ? 'مفتوحة' : statusFilter === 'CLOSED' ? 'مقفولة' : ''}</div>
          ) : (
            identityRows.map((row) => {
              const t = row.representative;
              const isSelected = selected?.contact_phone === t.contact_phone;
              return (
                <button
                  key={row.key}
                  onClick={() => setSelected(t)}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'right',
                    padding: 12,
                    border: 'none',
                    borderBottom: '1px solid var(--border)',
                    background: isSelected ? 'rgba(0,144,145,0.08)' : 'transparent',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                    <strong>{t.contact_name}</strong>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      {row.count > 1 && (
                        <span className="badge" style={{ background: '#eef2ff', color: '#3730a3' }}>{row.count} استفسارات</span>
                      )}
                      {!row.anyOpen && (
                        <span className="badge" style={{ background: '#e5e7eb', color: '#4b5563' }}>مقفول</span>
                      )}
                      {row.unread > 0 && row.anyOpen && (
                        <span className="badge" style={{ background: '#d94a4a', color: '#fff' }}>{row.unread}</span>
                      )}
                    </div>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                    {t.contact_phone} · {formatDateTime(row.latestAt)}
                  </div>
                </button>
              );
            })
          )}
        </div>

        <div className="card" style={{ display: 'flex', flexDirection: 'column' }}>
          {!selected ? (
            <div className="empty">اختر محادثة</div>
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8, gap: 8 }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <strong>{selected.contact_name}</strong>
                    {selected.status === 'CLOSED' ? (
                      <span className="badge" style={{ background: '#e5e7eb', color: '#4b5563' }}>مقفول</span>
                    ) : (
                      <span className="badge badge-success">مفتوح</span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                    {selected.contact_phone}
                  </div>
                  {/* Per-thread opened/closed + who closed is now
                      rendered inside each thread divider in the
                      timeline below (operator ask 2026-09-17) —
                      one identity may span multiple inquiries with
                      different open/close windows, so a single
                      header line couldn't reflect all of them. */}
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {selected.status === 'OPEN' ? (
                    <button
                      className="btn btn-small"
                      style={{ background: '#4b5563', color: '#fff' }}
                      onClick={() => void closeThread()}
                      disabled={closing}
                    >
                      {closing ? 'بيقفل...' : 'قفل الاستفسار'}
                    </button>
                  ) : (
                    // With the 2026-09-11 model, close is final for this
                    // thread row. A returning donor starts a fresh
                    // inquiry which lands as a NEW thread — the list
                    // groups it under the same name+phone identity, so
                    // the admin still sees one row per person.
                    <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                      الاستفسار ده مقفول. لو الزائر بعت تاني هيتفتح استفسار جديد تحت نفس الاسم والرقم.
                    </span>
                  )}
                </div>
              </div>
              <div style={{ flex: 1, overflow: 'auto', padding: 8, background: 'var(--surface-muted, #f7f7f7)', borderRadius: 8, marginBottom: 8 }}>
                {timeline.map((item, i) => {
                  if (item.kind === 'day') {
                    return (
                      <div
                        key={`day-${i}`}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 12,
                          margin: '18px 4px 12px',
                          color: 'var(--text-secondary)',
                          fontSize: 12,
                          fontWeight: 600,
                        }}
                      >
                        <span style={{ flex: 1, height: 1, background: 'var(--border)' }} />
                        <span
                          style={{
                            padding: '4px 12px',
                            background: '#fff',
                            border: '1px solid var(--border)',
                            borderRadius: 999,
                          }}
                        >
                          {item.label}
                        </span>
                        <span style={{ flex: 1, height: 1, background: 'var(--border)' }} />
                      </div>
                    );
                  }
                  if (item.kind === 'thread') {
                    const t = item.thread;
                    return (
                      <div
                        key={`thread-${i}`}
                        style={{
                          margin: '24px 4px 16px',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 6,
                        }}
                      >
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 12,
                          fontSize: 12,
                          fontWeight: 700,
                        }}>
                          <span style={{ flex: 1, height: 2, background: 'linear-gradient(90deg, transparent, var(--teal, #009091))', borderRadius: 2 }} />
                          <span
                            style={{
                              padding: '6px 14px',
                              background: 'var(--teal, #009091)',
                              color: '#fff',
                              borderRadius: 999,
                              boxShadow: '0 4px 12px rgba(0,144,145,0.30)',
                              letterSpacing: '0.3px',
                            }}
                          >
                            استفسار {t.status === 'CLOSED' ? 'مقفول' : 'مفتوح'} · {formatDate(t.opened_at)}
                          </span>
                          <span style={{ flex: 1, height: 2, background: 'linear-gradient(90deg, var(--teal, #009091), transparent)', borderRadius: 2 }} />
                        </div>
                        <div style={{
                          fontSize: 11,
                          color: 'var(--text-secondary)',
                          textAlign: 'center',
                          lineHeight: 1.7,
                        }}>
                          اتفتح: {formatDateTime(t.opened_at)}
                          {t.closed_at && (
                            <>
                              {' · '}اتقفل: {formatDateTime(t.closed_at)}
                              {t.closed_by && <> · بواسطة: <strong>{nameFor(t.closed_by)}</strong></>}
                            </>
                          )}
                        </div>
                      </div>
                    );
                  }
                  const m = item.msg;
                  return (
                    <div key={m.id} style={{ display: 'flex', justifyContent: m.sender === 'admin' ? 'flex-end' : 'flex-start', marginBottom: 6 }}>
                      <div style={{
                        background: m.sender === 'admin' ? 'var(--teal, #009091)' : '#fff',
                        color: m.sender === 'admin' ? '#fff' : 'inherit',
                        padding: '8px 12px',
                        borderRadius: 12,
                        maxWidth: '70%',
                        border: m.sender === 'admin' ? 'none' : '1px solid var(--border)',
                      }}>
                        <div>{m.text}</div>
                        <div style={{ fontSize: 10, opacity: 0.7, marginTop: 4 }}>{formatDateTime(m.created_at)}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {selected.status === 'OPEN' ? (
                <form onSubmit={send} style={{ display: 'flex', gap: 8 }}>
                  <input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="اكتب ردك..." style={{ flex: 1 }} />
                  <button type="submit" className="btn" disabled={!draft.trim()}>إرسال</button>
                </form>
              ) : (
                <div className="empty" style={{ padding: 12 }}>
                  الاستفسار ده اتقفل — أي رسالة جديدة من نفس الزائر هتنزل كاستفسار جديد تحت نفس الاسم والرقم.
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
